import { useCallback, useMemo, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import useDebouncedCallback from '../../../../../../../hooks/useDebouncedCallback';
import DropdownButton from '../../../../../../generic/DropdownButton';
import Flex from '../../../../../../generic/Flex';
import { STANDARD_COUNT_OPTIONS } from '../../../../../../generic/Pagination/consts';
import PaginationFooterCount from '../../../../../../generic/Pagination/PaginationFooterCount';
import PaginationRangeWithControl from '../../../../../../generic/Pagination/PaginationRangeWithControl';
import SidebarLayout from '../../../../../../layouts/SidebarLayout';
import { useLabelsSelectOptions } from '../../../hooks/useLabels';
import useSelectProjects from '../../../hooks/useSelectProjects';
import useTeamsSelectOptionsWithFilterUpdate from '../../../hooks/useTeams';
import type { ProjectsFilter } from '../../../../../../../../shared/projectsV2/filters';
import type { Project } from '../../../../../../../../shared/projectsV2/response';
import type { AddedProject } from '../../../../../../../shared/branches/types';
import type { ReleaseGroupState } from '../../../hooks/useReleaseGroupState';
import type { AddedProjectIndex, ProjectSelection } from '../../types';

import ProjectsFilters from './ProjectsFilters/ProjectsFilters';
import ProjectSortingDropdown from './ProjectSortingDropdown/ProjectSortingDropdown';
import ProjectsTable from './ProjectsTable/ProjectsTable';
import ProjectsTableSearchBar from './ProjectsTable/ProjectsTableSearchBar/ProjectsTableSearchBar';
import SelectedProjectsCountBanner from './SelectedProjectsCountBanner/SelectedProjectsCountBanner';
import ShowOnlyAddedProjectsToggle from './ShowOnlyAddedProjectsToggle/ShowOnlyAddedProjectsToggle';

import styles from './ProjectsSection.module.less';

type PropsFromReleaseGroupState =
  | 'sort'
  | 'setSort'
  | 'page'
  | 'setPage'
  | 'count'
  | 'setCount';

type Props = {
  shouldShowOnlyAddedProjects: boolean;
  setShouldShowOnlyAddedProjects: (show: boolean) => void;
  areProjectsLoading: boolean;
  projectCount: number;
  projects: Project[];
  projectErrors: string[] | null;
  addedProjects: AddedProjectIndex;
  onUpdateAddedProject: (project: AddedProject) => void;
  onAddProject: (projects: Project[]) => void;
  onRemoveProject: (projects: Project[]) => void;
  onAddAllProjects: () => void;
  onRemoveAllProjects: () => void;
  filters: ProjectsFilter;
  setFilters: (filter: ProjectsFilter) => void;
  onSelectLatestRevisionForSelectedProjects: (selectedProjects: ProjectSelection) => void;
} & Pick<ReleaseGroupState, PropsFromReleaseGroupState>;

const ProjectsSection: VoidFunctionComponent<Props> = (props) => {
  //  Get the list of just project locators
  const projectLocators = useMemo(() => props.projects.map((project) => project.id), [props.projects]);
  const selectProjects = useSelectProjects(projectLocators);

  const [searchValue, setSearchValue] = useState<string>('');

  //  Changes the filters AND resets the selected projects
  const onFiltersChange = useCallback((filters: ProjectsFilter) => {
    props.setFilters(filters);
    selectProjects.onClearSelectAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This is actually correct as is.
  }, [props.setFilters, selectProjects.onClearSelectAll]);
  const onFiltersChangeDebounced = useDebouncedCallback(onFiltersChange);

  //  Changes the sort AND resets the selected projects
  const onSortChange = useCallback((sort: ReleaseGroupState['sort']) => {
    props.setSort(sort);
    selectProjects.onClearSelectAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.setSort]);

  const onProjectSearch = (title: string) => {
    setSearchValue(title);
    onFiltersChangeDebounced({ ...props.filters, title });
  };

  const labelsFilters = useLabelsSelectOptions(onFiltersChangeDebounced);
  const teamsFilters = useTeamsSelectOptionsWithFilterUpdate(onFiltersChangeDebounced);

  return (
    <span>
      <SidebarLayout hasPaddingTop={false}>
        <SidebarLayout.Header>
          <div className={styles.title}>Projects</div>
          <div className={styles.description}>
            Select the projects to add to this release group along with their branch and/or revision
          </div>
          <SelectedProjectsCountBanner
            //  Note: this isn't very performant, but it's fine for now
            //  We should really use a Map for `addedProjects`, since we can easily do Map.size.
            addedProjectsCount={Object.keys(props.addedProjects).length}
          />
          <Flex direction="row" justify="space-between" align="flex-end" gap={12}>
            <div className={styles.headers}>
              <ProjectsTableSearchBar onProjectSearch={onProjectSearch} projectSearch={searchValue} />
              <ProjectSortingDropdown sort={props.sort} setSort={onSortChange} />
              <ShowOnlyAddedProjectsToggle
                isChecked={props.shouldShowOnlyAddedProjects}
                onToggle={props.setShouldShowOnlyAddedProjects}
              />
            </div>
            <div className={styles.sidebarPaginationContainer}>
              <DropdownButton
                id="fossa-pagination-footer-count-dropdown"
                title={`${props.count} per page`}
                // @ts-expect-error -- This works correctly despite TS's complaints.
                onSelect={props.setCount}
                pullRight
                dropup
              >
                {STANDARD_COUNT_OPTIONS.map((countOption) => (
                  <DropdownButton.Item key={countOption} eventKey={countOption} active={props.count === countOption}>
                    {countOption} per page
                  </DropdownButton.Item>
                ))}
              </DropdownButton>
              <span className={styles.sidebarPaginationControls}>
                <PaginationRangeWithControl
                  page={props.page}
                  setPage={props.setPage}
                  count={props.count}
                  totalItems={props.projectCount}
                />
              </span>
            </div>
          </Flex>
        </SidebarLayout.Header>
        <SidebarLayout.Main>
          <ProjectsTable
            projects={props.projects}
            areProjectsLoading={props.areProjectsLoading}
            projectErrors={props.projectErrors}
            totalProjectCount={props.projectCount}
            addedProjects={props.addedProjects}
            selectProjects={selectProjects}
            pageCount={props.count}
            filters={props.filters}
            onUpdateAddedProject={props.onUpdateAddedProject}
            onAddProjects={props.onAddProject}
            onRemoveProjects={props.onRemoveProject}
            onAddAllProjects={props.onAddAllProjects}
            onRemoveAllProjects={props.onRemoveAllProjects}
            onSelectLatestRevisionForSelectedProjects={props.onSelectLatestRevisionForSelectedProjects}
            onLabelClick={labelsFilters.handleLabelsChange}
            selectedLabels={labelsFilters.selectedLabelOptions}
            onTeamClick={teamsFilters.onTeamsSelected}
            selectedTeams={teamsFilters.selectedTeamOptions}
          />
          <PaginationFooterCount
            page={props.page}
            count={props.count}
            setCount={props.setCount}
            totalItems={props.projectCount}
          />
        </SidebarLayout.Main>
        <SidebarLayout.Sidebar>
          {/* NOTE: This wrapper div is required for the SidebarLayout to work correctly. */}
          <div>
            <div className={styles.sidebarContent}>
              <ProjectsFilters
                setFilters={onFiltersChange}
                filters={props.filters}
                labelsFilters={labelsFilters}
                teamsFilters={teamsFilters}
              />
            </div>
          </div>
        </SidebarLayout.Sidebar>
      </SidebarLayout>
    </span>
  );
};

export default ProjectsSection;
